import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  SessionType,
  getBaseUrl,
  logRocketIdentifyAdvisorUser,
  logRocketIdentifyClientUser,
} from '@shared';
import { FullScreenLoadingOverlay } from './FullScreenLoadingOverlay';
import type { ChildrenProps } from '../types/ChildrenProps';
import {
  closeSession,
  fetchSessionForAuthRequest,
  selectSession,
  storeAuth0Session,
} from '../model';
import { Login } from '../pages/Login';
import { DuplicateAccountScreen } from '@client/pages/DuplicateAccountScreen';
import { getPublicRoutePaths } from '../Router';
import { setAuthToken, clearAuthToken } from '@client/api/queryClient';
import { useSignOut } from './hooks/use-sign-out';
import { useClientRoutes } from '../hooks/useClientRoutes';
import { LoginRedirect } from './LoginRedirect';
import { getStoredImpersonationTarget, initImpersonation } from '@client/model/impersonationModel';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';

export function SessionLoader({ children }: ChildrenProps) {
  const session = useSelector(selectSession);
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, logout } = useAuth0();
  const { isClerkSignedIn, isClerkLoading, clerkEmail, clerkUserId, getClerkToken } =
    useClerkClientAuth();
  const signOut = useSignOut();
  const { isPublicOnboardingRoute } = useClientRoutes();

  // Check if current path is a public route
  const isClientLoginRoute = location.pathname === '/client/login';
  const publicRoutes = getPublicRoutePaths();
  const isPublicRoute =
    isClientLoginRoute || publicRoutes.some(route => location.pathname.startsWith(route));

  const isAuthenticatedAndUserLoaded =
    (isAuthenticated && user && user.sub) || (isClerkSignedIn && clerkUserId);
  const isLoadingOrSessionLoading = isLoading || isClerkLoading || session.isLoading;

  useEffect(() => {
    // load auth0 session (advisor flow)
    async function loadAuth0Session() {
      if (!isAuthenticated || !user?.sub) {
        return;
      }
      try {
        const authUserId = user.sub!;
        const accessToken = await getAccessTokenSilently();
        setAuthToken(accessToken);
        dispatch(
          storeAuth0Session({
            authUserId,
            accessToken,
          }),
        );

        // Restore impersonation Redux state from localStorage if it was active before page reload
        const storedTarget = getStoredImpersonationTarget();
        if (storedTarget) {
          dispatch(initImpersonation(storedTarget));
        }

        dispatch(fetchSessionForAuthRequest());
      } catch (error) {
        clearAuthToken();
        dispatch(closeSession());
      }
    }

    // load clerk session (client flow)
    async function loadClerkSession() {
      if (!isClerkSignedIn || !clerkUserId) {
        return;
      }
      try {
        const accessToken = await getClerkToken();
        if (!accessToken) {
          clearAuthToken();
          dispatch(closeSession());
          return;
        }
        setAuthToken(accessToken);
        dispatch(
          storeAuth0Session({
            authUserId: clerkUserId,
            accessToken,
          }),
        );
        dispatch(fetchSessionForAuthRequest());
      } catch (error) {
        clearAuthToken();
        dispatch(closeSession());
      }
    }

    if (session.sessionType === SessionType.unknown && !isLoadingOrSessionLoading) {
      if (isAuthenticated && user?.sub) {
        loadAuth0Session();
      } else if (isClerkSignedIn && clerkUserId) {
        loadClerkSession();
      } else if (!isLoading && !isClerkLoading) {
        clearAuthToken();
        dispatch(closeSession());
      }
    }
  }, [
    dispatch,
    session.sessionType,
    isAuthenticated,
    isLoading,
    user,
    session.isLoading,
    isClerkSignedIn,
    isClerkLoading,
    clerkUserId,
    getAccessTokenSilently,
    getClerkToken,
  ]);

  // Periodically refresh the Clerk token in Redux store
  // (Token provider registration is handled by ClerkAuthBridge)
  useEffect(() => {
    if (!isClerkSignedIn) {
      return;
    }

    // Clerk session tokens expire after ~60s — refresh Redux store every 50s
    const refreshInterval = setInterval(async () => {
      try {
        const freshToken = await getClerkToken();
        if (freshToken) {
          setAuthToken(freshToken);
          dispatch(storeAuth0Session({ authUserId: clerkUserId!, accessToken: freshToken }));
        }
      } catch {
        // Token refresh failed — session will handle on next API call
      }
    }, 50_000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [isClerkSignedIn, clerkUserId, getClerkToken, dispatch]);

  // identify user in logrocket
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Handle client users
    if (session.sessionType === SessionType.client && isAuthenticated) {
      const { auth0Session } = session;
      const auth0UserId = auth0Session?.authUserId;
      const clientEmail = auth0Session?.clientUser?.email;
      if (auth0UserId && clientEmail) {
        logRocketIdentifyClientUser(auth0UserId, clientEmail);
      }
      return;
    }

    // Handle advisor users
    if (session?.advisor?.advisorId) {
      const isWizardImpersonating =
        session.sessionType === SessionType.wizard && session.impersonation?.isImpersonating;
      const advisorId = isWizardImpersonating
        ? session.impersonation?.wizardAdvisorId
        : session.advisor.advisorId;
      const advisorName = isWizardImpersonating
        ? session.impersonation?.wizardAdvisorFirstName ||
          session.impersonation?.wizardEmail ||
          'Wizard'
        : session.advisor.advisorFirstName;

      if (advisorId) {
        logRocketIdentifyAdvisorUser(advisorId, advisorName);
      }
      return;
    }
  }, [
    session.sessionType,
    session.advisor,
    session.auth0Session,
    session.impersonation,
    isAuthenticated,
    isLoading,
  ]);

  // Logout from Auth0 when session is expired.
  // Guard against triggering during session initialization to prevent race condition:
  // After signup redirect, Auth0 reports isAuthenticated=true but the session API call
  // may still be in progress or may have transiently failed. Only trigger signOut when
  // we're certain the session has genuinely expired (not during initial load).
  const isSessionInitializing = session.isLoading || session.isInvalidating;
  useEffect(() => {
    if (
      session.sessionType === SessionType.unauthenticated &&
      isAuthenticated &&
      !isLoading &&
      !isPublicOnboardingRoute &&
      !isSessionInitializing
    ) {
      // Skip Auth0 session expiry handling for Clerk-authenticated client users
      if (isClerkSignedIn) {
        return;
      }

      signOut();
    }
  }, [
    session.sessionType,
    isAuthenticated,
    isLoading,
    signOut,
    isPublicOnboardingRoute,
    isSessionInitializing,
    isClerkSignedIn,
    dispatch,
    logout,
  ]);

  if (session.sessionType === SessionType.unknown || isLoadingOrSessionLoading) {
    if (isPublicOnboardingRoute || isPublicRoute) {
      return children;
    }
    return <FullScreenLoadingOverlay isLoading={true} />;
  }

  if (session.sessionType === SessionType.duplicateAccount) {
    return <DuplicateAccountScreen />;
  }

  // Unauthenticated gate - the single auth decision point
  if (!isAuthenticatedAndUserLoaded || session.sessionType === SessionType.unauthenticated) {
    // Public routes (signup, specs, etc.) render without auth
    if (isPublicRoute || isPublicOnboardingRoute) {
      return children;
    }
    // If on root with screen_hint=signup, redirect to signup page (e.g. from Framer signup link)
    if (location.pathname === '/') {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('screen_hint') === 'signup') {
        return <Navigate to={`/signup${location.search}`} replace />;
      }
      return <Login />;
    }
    // Otherwise redirect to login with preserved params
    return <LoginRedirect />;
  }

  return session.sessionType !== SessionType.unknown ? children : null;
}
